// SortableJS for page lineup, story items, and journal merge (stock desktop feel).
// jQuery UI map: connectWith → group; placeholder → ghostClass; sortstart/sort/sortstop → onStart/onMove/onEnd;
// forcePlaceholderSize → lockPlaceholderBox; changeMouseCursor; handleMerging (was droppable drop).

const Sortable = require('sortablejs')
const lineup = require('./lineup')
const state = require('./state')
const plugin = require('./plugin')
const active = require('./active')
const pageHandler = require('./pageHandler')
const random = require('./random')
const { pageEmitter } = require('./page')

const DRAG_CLASS = 'wiki-page-drag'

// Edge pan: start scrolling early; speed ramps toward the hard edge.
const EDGE_ZONE_PX = 150
const MAX_SCROLL_PX_PER_SEC = 1500
const VERT_EDGE_PX = 120
const VERT_SCROLL_PX_PER_SEC = 1100

const eventXY = e => {
  if (!e) return { x: null, y: null }
  if (typeof e.clientX === 'number') return { x: e.clientX, y: e.clientY }
  const t = e.touches?.[0] || e.changedTouches?.[0]
  return { x: t?.clientX ?? null, y: t?.clientY ?? null }
}

const getMain = () => document.querySelector('.main')

const pagesInMain = main => (main ? [...main.querySelectorAll(':scope > .page')] : [])

const centerInMain = (el, main) => {
  if (!main || !el) return
  const mainRect = main.getBoundingClientRect()
  const elRect = el.getBoundingClientRect()
  const delta = elRect.left + elRect.width / 2 - (mainRect.left + mainRect.width / 2)
  if (Math.abs(delta) > 1) main.scrollLeft += delta
}

const nearestPageIndexIn = main => {
  const nodes = pagesInMain(main)
  if (!nodes.length || !main) return -1
  const mainRect = main.getBoundingClientRect()
  const mid = mainRect.left + mainRect.width / 2
  let best = 0
  let bestDist = Infinity
  nodes.forEach((n, i) => {
    const r = n.getBoundingClientRect()
    const d = Math.abs(r.left + r.width / 2 - mid)
    if (d < bestDist) {
      bestDist = d
      best = i
    }
  })
  return best
}

const scrollLineupNearEdge = (clientX, main) => {
  if (!main || typeof clientX !== 'number') return { dir: 0, speed: 0 }
  const mainRect = main.getBoundingClientRect()
  const distLeft = clientX - mainRect.left
  const distRight = mainRect.right - clientX
  let dir = 0
  let near = Infinity
  if (distLeft <= EDGE_ZONE_PX && distLeft <= distRight) {
    dir = -1
    near = Math.max(0, distLeft)
  } else if (distRight <= EDGE_ZONE_PX) {
    dir = 1
    near = Math.max(0, distRight)
  }
  if (!dir) return { dir: 0, speed: 0 }
  const t = 1 - near / EDGE_ZONE_PX
  return { dir, speed: MAX_SCROLL_PX_PER_SEC * t * t }
}

// Vertical edge pan while dragging items: ramp inside the zone, keep accelerating
// past the page edge so tall stories are reachable without tiny nudges.
const scrollPageNearEdge = (els, pointerY, dt, zonePx, maxPxPerSec) => {
  if (typeof pointerY !== 'number' || !(zonePx > 0) || !(maxPxPerSec > 0)) return
  for (const el of els) {
    if (!el || el.scrollHeight <= el.clientHeight + 2) continue
    const r = el.getBoundingClientRect()
    let dir = 0
    // 0 at inner zone edge, 1 at the page edge, >1 when pointer is beyond the page.
    let depth = 0
    if (pointerY < r.top + zonePx) {
      dir = -1
      depth = (r.top + zonePx - pointerY) / zonePx
    } else if (pointerY > r.bottom - zonePx) {
      dir = 1
      depth = (pointerY - (r.bottom - zonePx)) / zonePx
    }
    if (!dir || depth <= 0) continue
    const t = Math.min(2.5, depth)
    const speed = maxPxPerSec * t * t
    if (speed > 0) el.scrollTop += dir * speed * dt
  }
}

const pageAtClientX = clientX => {
  if (typeof clientX !== 'number') return null
  for (const page of document.querySelectorAll('.main > .page')) {
    const r = page.getBoundingClientRect()
    if (clientX >= r.left && clientX <= r.right) return page
  }
  return null
}

const vertScrollCandidates = (pointerX, sourcePage, destStory) => {
  const out = []
  const add = el => {
    if (el && !out.includes(el)) out.push(el)
  }
  add(pageAtClientX(pointerX))
  add(destStory?.closest?.('.page'))
  add(sourcePage)
  return out
}

// Body-appended drag clones miss .page typography — copy resolved metrics from
// the source item only (not .page — that computed size is often wrong here).
const DRAG_TYPO_PROPS = [
  'fontSize',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'lineHeight',
  'letterSpacing',
  'wordSpacing',
  'textTransform',
  'fontVariant',
  'fontStretch',
]

const DRAG_TYPO_DESCENDANTS =
  'p, h1, h2, h3, h4, h5, h6, blockquote, li, td, th, pre, code, .paragraph'

const syncDragItemTypography = function (sourceEl, targetEl) {
  if (!sourceEl || !targetEl) return
  const cs = getComputedStyle(sourceEl)
  targetEl.style.setProperty('-webkit-text-size-adjust', '100%')
  targetEl.style.setProperty('text-size-adjust', '100%')
  for (const prop of DRAG_TYPO_PROPS) {
    targetEl.style[prop] = cs[prop]
  }
  const srcTexts = sourceEl.querySelectorAll(DRAG_TYPO_DESCENDANTS)
  const dstTexts = targetEl.querySelectorAll(DRAG_TYPO_DESCENDANTS)
  for (let i = 0; i < srcTexts.length && i < dstTexts.length; i++) {
    const tcs = getComputedStyle(srcTexts[i])
    for (const prop of DRAG_TYPO_PROPS) {
      dstTexts[i].style[prop] = tcs[prop]
    }
  }
}

const clamp = (value, min, max) => Math.max(min, Math.min(value, max))

const withDragArtifactsHidden = function (fn) {
  const hidden = []
  document
    .querySelectorAll(
      'body > .ui-sortable-helper.item, body > .sortable-fallback.item, .wiki-item-drag.item, .item-placeholder, .page.ui-sortable-placeholder, .sortable-fallback.page, .wiki-page-ghost',
    )
    .forEach(el => {
      hidden.push([el, el.style.visibility])
      el.style.visibility = 'hidden'
    })
  try {
    return fn()
  } finally {
    hidden.forEach(([el, vis]) => {
      el.style.visibility = vis
    })
  }
}

const pageDropBounds = function (pageEl) {
  const story = pageEl.querySelector('.story')
  const paper = pageEl.querySelector('.paper')
  if (!story) return null
  const pageRect = pageEl.getBoundingClientRect()
  const storyRect = story.getBoundingClientRect()
  const bottomRect = paper?.getBoundingClientRect() || pageRect
  // Full page column (handle, title/header, story, journal) — not story-only.
  // Illegal-drop cursor must follow the pointer over journal/header too.
  return {
    story,
    storyRect,
    bandTop: pageRect.top,
    bandBottom: bottomRect.bottom,
    bandLeft: pageRect.left,
    bandRight: pageRect.right,
  }
}

const nearestPageDropBounds = function (x, y, requireSortable) {
  let best = null
  let bestDist = Infinity
  document.querySelectorAll('.page').forEach(pageEl => {
    const band = pageDropBounds(pageEl)
    if (!band) return
    if (requireSortable && !band.story._wikiSortable) return
    const anchorX = clamp(x, band.bandLeft, band.bandRight)
    const anchorY = clamp(y, band.bandTop, band.bandBottom)
    const dist = (x - anchorX) ** 2 + (y - anchorY) ** 2
    if (dist < bestDist) {
      bestDist = dist
      best = { ...band, anchorX, anchorY }
    }
  })
  return best
}

const nearestPageIndexAt = function (main, x, y) {
  const nodes = pagesInMain(main)
  if (!nodes.length || typeof x !== 'number' || typeof y !== 'number') return -1
  let best = -1
  let bestDist = Infinity
  nodes.forEach((pageEl, i) => {
    const band = pageDropBounds(pageEl)
    if (!band) return
    const anchorX = clamp(x, band.bandLeft, band.bandRight)
    const anchorY = clamp(y, band.bandTop, band.bandBottom)
    const dist = (x - anchorX) ** 2 + (y - anchorY) ** 2
    if (dist < bestDist) {
      bestDist = dist
      best = i
    }
  })
  return best
}

const resolveStoryDropPointer = function (x, y) {
  const band = nearestPageDropBounds(x, y, true)
  if (!band) return null
  const { story, storyRect, anchorY } = band
  // Vertical list: insert follows Y only. Finger X is often on the left handle
  // or near the page edge; probing there misses .item content and Sortable
  // stops updating the placeholder.
  const probeX = storyRect.left + storyRect.width / 2
  const belowStory = anchorY >= storyRect.bottom - 1
  const probeY = belowStory ? storyRect.bottom - 1 : clamp(anchorY, storyRect.top + 1, storyRect.bottom - 1)
  return {
    story,
    sortable: story._wikiSortable,
    x: probeX,
    y: probeY,
    forceEnd: belowStory,
  }
}

const storyInsertAnchor = function (story) {
  if (!story) return null
  return story.querySelector(':scope > .item-placeholder') || null
}

// Keep a mid-list insert parked: invertSwap with a full threshold re-flips as soon as
// the compact slot shifts a short neighbor under the finger (Tip + one-liners on
// How To Wiki). Expand the stick zone into the nearer half of each neighbor.
// Stick to the in-list placeholder — never the floating forceFallback helper.
// The helper follows the pointer, so a helper-based zone never releases and
// continuous nudge cannot move the placeholder to a new gap (desktop + mobile).
const isStoryInsertSticky = function (story, y) {
  if (!Sortable.active || typeof y !== 'number' || !story) return false
  const anchor = storyInsertAnchor(story)
  if (!anchor) return false
  const rect = anchor.getBoundingClientRect()
  if (!rect || (!rect.height && !rect.width)) return false
  let prev = anchor.previousElementSibling
  while (prev && (prev.classList.contains('shadow-copy') || prev.style?.display === 'none')) {
    prev = prev.previousElementSibling
  }
  let next = anchor.nextElementSibling
  while (next && (next.classList.contains('shadow-copy') || next.style?.display === 'none')) {
    next = next.nextElementSibling
  }
  const prevH = prev?.classList?.contains('item') ? prev.getBoundingClientRect().height : 0
  const nextH = next?.classList?.contains('item') ? next.getBoundingClientRect().height : 0
  const padBefore = Math.max(20, prevH * 0.5)
  const padAfter = Math.max(20, nextH * 0.5)
  return y >= rect.top - padBefore && y <= rect.bottom + padAfter
}

const nudgeStorySortableAt = function (x, y, onTarget) {
  if (!Sortable.dragged || !Sortable.active) return
  const resolved = resolveStoryDropPointer(x, y)
  if (!resolved) return
  const { story, sortable, x: probeX, y: probeY, forceEnd } = resolved
  // Already in the aimed gap (or the neighbor half that means the same gap):
  // do not re-probe — continuous nudge was undoing mid-list snaps.
  if (!forceEnd && isStoryInsertSticky(story, probeY)) {
    if (onTarget) onTarget(story)
    return
  }
  withDragArtifactsHidden(() => {
    let target = document.elementFromPoint(probeX, probeY)
    if (!story.contains(target) || forceEnd) {
      const dragEl = Sortable.dragged
      const items = [...story.querySelectorAll(':scope > .item:not(.shadow-copy)')]
      target = items.filter(el => el !== dragEl).at(-1) || story
    }
    sortable._onDragOver({
      clientX: probeX,
      clientY: probeY,
      target,
      rootEl: story,
    })
    if (onTarget) onTarget(story)
  })
}

const bindMainSortable = function (mainEl) {
  if (!mainEl) return null
  if (mainEl._wikiPageSortable) {
    mainEl._wikiPageSortable.destroy()
    mainEl._wikiPageSortable = null
  }

  let originalPageIndex = null
  let $dragging = null
  let lastPointerX = null
  let lastPointerY = null
  let capturedEl = null
  let capturedPointerId = null

  // Only the body-appended drag clone — never Sortable.ghost (in-list slot).
  const helperEl = () =>
    document.querySelector(`body > .sortable-fallback.page, body > .${DRAG_CLASS}.page`)

  const prepareDesktopHelper = function (el) {
    if (!el?.classList?.contains('page')) return
    if (el.parentElement !== document.body) return
    el.classList.remove('wiki-page-ghost')
    el.style.overflow = 'visible'
    el.style.visibility = 'visible'
    el.style.background = '#fff'
    el.style.setProperty('opacity', '1', 'important')
    for (const child of el.children) {
      child.style.visibility = 'visible'
      child.style.opacity = ''
    }
  }

  const pageNodes = () => pagesInMain(mainEl)

  const capturePointer = function (oe) {
    if (!oe || typeof oe.pointerId !== 'number') return
    const el = document.body
    try {
      el.setPointerCapture(oe.pointerId)
      capturedEl = el
      capturedPointerId = oe.pointerId
    } catch (_) {
      try {
        oe.target?.setPointerCapture(oe.pointerId)
        capturedEl = oe.target
        capturedPointerId = oe.pointerId
      } catch (_) {
        /* not a pointer event */
      }
    }
  }

  const setPendingRemove = function (on) {
    if (!$dragging?.length) return
    const fallback = helperEl()
    $dragging.toggleClass('pending-remove', !!on)
    if (!fallback) return
    fallback.classList.toggle('pending-remove', !!on)
    fallback.classList.remove('wiki-page-ghost')
    fallback.style.transition = 'opacity 300ms'
    fallback.style.setProperty('opacity', on ? '0.2' : '1', 'important')
  }

  const updatePendingRemove = function (e) {
    if (!$dragging?.length || !$dragging.hasClass('page')) return
    const canRemove = pageNodes().length > 1
    const { y: clientY } = eventXY(e)
    const pageY = typeof e?.pageY === 'number' ? e.pageY : e?.touches?.[0]?.pageY ?? e?.changedTouches?.[0]?.pageY
    const aboveWindow =
      (typeof clientY === 'number' && clientY <= 0) || (typeof pageY === 'number' && pageY < 0)
    setPendingRemove(canRemove && aboveWindow)
  }

  const onPointerMove = function (e) {
    const { x, y } = eventXY(e)
    if (typeof x === 'number') lastPointerX = x
    if (typeof y === 'number') lastPointerY = y
    updatePendingRemove(e)
  }

  let pageDragNudgeTimer = null

  const nudgePageSortableDesktop = function () {
    if (!$dragging?.length || !$dragging.hasClass('page')) return
    if ($dragging.hasClass('pending-remove')) return
    if (typeof lastPointerX !== 'number' || typeof lastPointerY !== 'number') return
    const mainRect = mainEl.getBoundingClientRect()
    if (lastPointerY <= mainRect.top || lastPointerY <= 0) return
    const sortable = mainEl._wikiPageSortable
    if (!sortable) return
    const probeX = lastPointerX
    const probeY = clamp(lastPointerY, mainRect.top + 12, mainRect.bottom - 12)
    withDragArtifactsHidden(() => {
      const target = document.elementFromPoint(probeX, probeY) || mainEl
      sortable._onDragOver({
        clientX: probeX,
        clientY: probeY,
        target,
        rootEl: mainEl,
      })
    })
  }

  const stopTracking = function () {
    if (pageDragNudgeTimer != null) {
      clearInterval(pageDragNudgeTimer)
      pageDragNudgeTimer = null
    }
    document.removeEventListener('pointermove', onPointerMove, true)
    $dragging = null
    if (capturedEl && capturedPointerId != null) {
      try {
        capturedEl.releasePointerCapture(capturedPointerId)
      } catch (_) {
        /* already released */
      }
    }
    capturedEl = null
    capturedPointerId = null
  }

  const finishDrag = function ($page, removing) {
    if (!$page.hasClass('page')) return
    const $pages = $('.page')
    let index = $pages.index($('.active'))
    let firstItemIndex = $('.item').index($page.find('.item')[0])
    if (removing) {
      if ($pages.length === 1) return
      lineup.removeKey($page.data('key'))
      $page.remove()
      active.set($('.page')[index], true)
    } else {
      $page.removeClass('pending-remove')
      const newIndex = pageNodes().indexOf($page[0])
      lineup.changePageIndex($page.data('key'), newIndex >= 0 ? newIndex : index)
      active.set($page)
      if (originalPageIndex != null && originalPageIndex < (newIndex >= 0 ? newIndex : index)) {
        index = originalPageIndex
        firstItemIndex = $('.item').index($($('.page')[index]).find('.item')[0])
      }
    }
    plugin.renderFrom(firstItemIndex)
    state.setUrl()
    if (window.debug) state.debugStates()
  }

  const sortable = Sortable.create(mainEl, {
    animation: 150,
    handle: '.page-handle',
    draggable: '.page',
    ghostClass: 'wiki-page-ghost',
    dragClass: DRAG_CLASS,
    direction: 'horizontal',
    forceFallback: true,
    fallbackOnBody: true,
    fallbackTolerance: 5,
    sort: true,
    group: {
      name: 'wiki-pages',
      pull: false,
      put: false,
    },
    // Desktop uses Sortable scroll; mobile drives edge-proximity scroller itself.
    scroll: mainEl,
    bubbleScroll: true,
    forceAutoScrollFallback: true,
    scrollSensitivity: 80,
    scrollSpeed: 18,
    onMove(evt) {
      if (!evt?.dragged?.classList?.contains('page')) return false
      if (evt.to && !evt.to.classList?.contains('main') && evt.to !== mainEl) return false
      return true
    },
    onStart(evt) {
      if (!$(evt.item).hasClass('page')) return
      active.set($(evt.item), true)
      originalPageIndex = pageNodes().indexOf(evt.item)
      $dragging = $(evt.item)
      const oe = evt.originalEvent
      const { x, y } = eventXY(oe)
      if (typeof x === 'number') lastPointerX = x
      if (typeof y === 'number') lastPointerY = y

      try {
        setStorySortableEnabled(false)
      } catch (_) {
        /* optional */
      }

      if (sortable.option) {
        sortable.option('scroll', mainEl)
        sortable.option('animation', 150)
        sortable.option('swapThreshold', 1)
        prepareDesktopHelper(helperEl())
        requestAnimationFrame(() => prepareDesktopHelper(helperEl()))
        capturePointer(oe)
      }

      document.addEventListener('pointermove', onPointerMove, true)
      pageDragNudgeTimer = setInterval(nudgePageSortableDesktop, 30)
    },
    onEnd(evt) {
      const $page = $(evt.item)
      const removing = $page.hasClass('pending-remove')
      stopTracking()
      try {
        setStorySortableEnabled(true)
      } catch (_) {
        /* optional */
      }
      // If a page somehow landed inside a story, put it back under .main.
      if ($page.parent().hasClass('story') || !$page.parent().hasClass('main')) {
        const kids = pageNodes()
        const ref = kids[Math.min(originalPageIndex ?? kids.length, kids.length)]
        if (ref) mainEl.insertBefore($page[0], ref)
        else mainEl.appendChild($page[0])
      }
      finishDrag($page, removing)
    },
  })

  mainEl._wikiPageSortable = sortable
  return sortable
}

const setPageSortableEnabled = function (enabled) {
  document.querySelectorAll('.main').forEach(mainEl => {
    if (mainEl._wikiPageSortable) {
      mainEl._wikiPageSortable.option('disabled', !enabled)
    }
  })
}

const PLACEHOLDER_SLOT_PX = 28


const getItem = function ($item) {
  if ($($item).length > 0) {
    return $($item).data('item') || $($item).data('staticItem')
  }
}

const assignNewItemId = function ($item, item) {
  if (!item.alias) item.alias = item.id
  item.id = random.itemId()
  $item.attr('data-id', item.id)
  $item.data('id', item.id)
  $item.data('item').id = item.id
}

const aliasItem = function ($page, $item, oldItem, opts = {}) {
  const item = $.extend({}, oldItem)
  $item.data('item', item)
  const pageObject = lineup.atKey($page.data('key'))
  const destHasId = pageObject.getItem(item.id) != null
  // Copies always mint a unique wiki id (random.itemId); keep alias to origin.
  if (destHasId || opts.asCopy) {
    assignNewItemId($item, item)
  } else if (item.alias != null) {
    if (pageObject.getItem(item.alias) == null) {
      item.id = item.alias
      delete item.alias
      $item.attr('data-id', item.id)
    }
  }
  return item
}

const equals = (a, b) => a && b && a.get(0) === b.get(0)

const getStoryItemOrder = ($story) =>
  $($story)
    .children('.item:not(.shadow-copy)')
    .map((_, value) => $(value).attr('data-id'))
    .get()

const clearContent = function ($item) {
  $item.children().remove()
  return $item
}

const commitSamePageReorder = function ($item, originalIndex, originalOrder) {
  const $destinationPage = $item.parents('.page:first')
  const order = getStoryItemOrder($item.parents('.story:first'))
  if (JSON.stringify(order) === JSON.stringify(originalOrder)) return
  $('.shadow-copy').remove()
  clearContent($item)
  let index = $('.item').index($item)
  if (originalIndex < index) {
    index = originalIndex
  }
  plugin.renderFrom(index)
  const item = getItem($item)
  pageHandler.put($destinationPage, { id: item.id, type: 'move', order })
}

const handleDrop = function (evt, $item, originalIndex, originalOrder, opts = {}) {
  let dragAttribution, index
  let item = getItem($item)
  const $sourcePage = $item.data('pageElement')
  const sourceIsReadOnly = $sourcePage.hasClass('ghost') || $sourcePage.hasClass('remote')
  const shiftKey = !!opts.shiftKey

  if (!$sourcePage.hasClass('ghost')) {
    dragAttribution = {
      page: $sourcePage.data().data['title'],
    }
    if ($sourcePage.data().site != null) {
      dragAttribution['site'] = $sourcePage.data().site
    }
  }

  const $destinationPage = $item.parents('.page:first')
  const destinationIsGhost = $destinationPage.hasClass('ghost')

  const moveWithinPage = equals($sourcePage, $destinationPage)
  const moveBetweenDuplicatePages =
    !moveWithinPage && !shiftKey && $sourcePage.attr('id') === $destinationPage.attr('id')

  const removedTo = {
    page: $destinationPage.data().data['title'],
  }

  if ((destinationIsGhost && !moveWithinPage) || moveBetweenDuplicatePages) {
    if (typeof opts.revert === 'function') opts.revert()
    return
  }

  if (moveWithinPage) {
    commitSamePageReorder($item, originalIndex, originalOrder)
    return
  }

  const copying = sourceIsReadOnly || shiftKey
  if (copying) {
    $('.shadow-copy')
      .removeClass('shadow-copy is-copy-preview')
      .addClass('item')
      .data('pageElement', $sourcePage)
      .data('item', $.extend({}, item))
      .data('id', item.id)
      .attr({ 'data-id': item.id })
      .css('display', '')
      .show()
  } else {
    pageHandler.put($sourcePage, { id: item.id, type: 'remove', removedTo })
  }

  $item.data('pageElement', $destinationPage)
  const $before = $item.prev('.item')
  const before = getItem($before)
  item = aliasItem($destinationPage, $item, item, { asCopy: copying })
  pageHandler.put($destinationPage, {
    id: item.id,
    type: 'add',
    item,
    after: before?.id,
    attribution: dragAttribution,
  })
  $('.shadow-copy').remove()
  clearContent($item)
  $before.after($item)
  index = $('.item').index($item)
  if (originalIndex < index) {
    index = originalIndex
  }
  plugin.renderFrom(index)
}

const applyItemDragCursor = function (cursor) {
  $('body').css('cursor', cursor)
  $('.wiki-item-drag, .sortable-fallback.item').css('cursor', cursor)
}

const clearItemDragCursorClasses = function () {
  document.body.classList.remove('wiki-item-story-dragging')
}

const changeMouseCursor = function (evt, $item, $toStory) {
  const $sourcePage = $item.data('pageElement')
  if (!$sourcePage?.length) return
  const sourceIsReadOnly = $sourcePage.hasClass('ghost') || $sourcePage.hasClass('remote')
  const $destinationPage = $($toStory).closest('.page')
  const destinationIsGhost = $destinationPage.hasClass('ghost')
  const moveWithinPage = equals($sourcePage, $destinationPage)
  const moveBetweenDuplicatePages = !moveWithinPage && $sourcePage.attr('id') === $destinationPage.attr('id')
  const copying = sourceIsReadOnly || (!!evt.shiftKey && !moveWithinPage)
  let cursor = 'move'
  if ((!moveWithinPage && destinationIsGhost) || (moveBetweenDuplicatePages && !evt.shiftKey)) {
    cursor = 'no-drop'
    $('.shadow-copy').hide().removeClass('is-copy-preview')
  } else if (copying) {
    cursor = 'copy'
    $('.shadow-copy').show().addClass('is-copy-preview')
  } else {
    cursor = 'move'
    $('.shadow-copy').hide().removeClass('is-copy-preview')
  }
  applyItemDragCursor(cursor)
}

const isEditOn = () =>
  document.body.classList.contains('fedwiki-edit-mode') || $('.editEnable').is(':visible')

const bindStorySortable = function ($page) {
  const storyEl = $page.find('.story')[0]
  if (!storyEl) return null
  if (storyEl._wikiSortable) {
    storyEl._wikiSortable.destroy()
    storyEl._wikiSortable = null
  }
  if (storyEl._wikiPlaceholderLock) {
    storyEl.removeEventListener('pointerdown', storyEl._wikiPlaceholderLock, true)
    storyEl._wikiPlaceholderLock = null
  }
  if (storyEl._wikiPlaceholderUnlock) {
    storyEl.removeEventListener('pointerup', storyEl._wikiPlaceholderUnlock, true)
    storyEl.removeEventListener('pointercancel', storyEl._wikiPlaceholderUnlock, true)
    storyEl._wikiPlaceholderUnlock = null
  }

  const origCursor = $('body').css('cursor')
  let originalOrder = null
  let originalIndex = null
  let dragCancelled = false
  let lastEvent = null
  let dragItemSnapshot = null
  let dragPageElement = null
  let shiftHeld = false
  let lastToStory = null
  let edgeScrollRaf = null
  let lastPointerY = null
  let lastPointerX = null
  let lastEdgeScrollTs = 0
  let placeholderBox = null

  const clearPlaceholderBox = function (el) {
    placeholderBox = null
    if (!el) return
    el.classList.remove('wiki-item-drag-sizing')
    el.style.height = ''
    el.style.minHeight = ''
    el.style.maxHeight = ''
    el.style.overflow = ''
    el.style.boxSizing = ''
    el.style.willChange = ''
  }

  const placeholderHeightPx = function (el) {
    return placeholderBox?.h || el?.offsetHeight || PLACEHOLDER_SLOT_PX
  }

  const lockPlaceholderBox = function (el) {
    if (!el || !placeholderBox) return
    const h = placeholderHeightPx(el)
    el.classList.add('wiki-item-drag-sizing')
    el.style.boxSizing = 'border-box'
    el.style.height = `${h}px`
    el.style.minHeight = `${h}px`
    el.style.maxHeight = `${h}px`
    el.style.overflow = 'hidden'
  }

  const reserveSlotMinHeight = function (el) {
    if (!el || !placeholderBox) return
    el.style.boxSizing = 'border-box'
    el.style.minHeight = `${placeholderHeightPx(el)}px`
  }

  const cancelDrag = function (e) {
    if (e.which === 27 && storyEl._wikiSortable) {
      dragCancelled = true
      document.dispatchEvent(new PointerEvent('pointerup', { bubbles: true }))
      document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }))
    }
  }

  // Pointer aim drives move/copy/no-drop (duplicate pages, ghosts) — not only onMove.
  const refreshDragCursor = function () {
    if (typeof lastPointerX !== 'number' || typeof lastPointerY !== 'number') return
    const resolved = resolveStoryDropPointer(lastPointerX, lastPointerY)
    const band = resolved ? null : nearestPageDropBounds(lastPointerX, lastPointerY, false)
    const toStory = resolved?.story || band?.story
    if (!toStory) return
    const dragEl =
      Sortable.dragged || document.querySelector('.wiki-item-drag, .sortable-fallback.item')
    if (!dragEl) return
    const $drag = $(dragEl)
    if (!$drag.data('pageElement') && dragPageElement) $drag.data('pageElement', dragPageElement)
    if (!$drag.data('item') && dragItemSnapshot) $drag.data('item', $.extend({}, dragItemSnapshot))
    lastToStory = toStory
    changeMouseCursor({ shiftKey: shiftHeld }, $drag, toStory)
  }

  const onShiftKey = function (e) {
    if (e.key !== 'Shift') return
    shiftHeld = e.type === 'keydown'
    if (lastEvent && typeof lastEvent === 'object') {
      try {
        lastEvent = { ...lastEvent, shiftKey: shiftHeld }
      } catch (_) {
        /* ignore */
      }
    }
    refreshDragCursor()
  }

  // Desktop item-drag page scroll (Sortable's built-in scroll misses .page).
  const stopEdgeScroll = function () {
    if (edgeScrollRaf != null) {
      cancelAnimationFrame(edgeScrollRaf)
      edgeScrollRaf = null
    }
    lastEdgeScrollTs = 0
  }

  const edgeScrollTick = function (ts) {
    edgeScrollRaf = null
    if (!Sortable.active || typeof lastPointerY !== 'number') return
    if (!lastEdgeScrollTs) lastEdgeScrollTs = ts
    const dt = Math.min(0.05, (ts - lastEdgeScrollTs) / 1000)
    lastEdgeScrollTs = ts

    const candidates = vertScrollCandidates(lastPointerX, dragPageElement?.[0], lastToStory)
    scrollPageNearEdge(candidates, lastPointerY, dt, VERT_EDGE_PX, VERT_SCROLL_PX_PER_SEC)
    if (typeof lastPointerX === 'number') {
      nudgeStorySortableAt(lastPointerX, lastPointerY, story => {
        lastToStory = story
        refreshDragCursor()
      })
    }

    edgeScrollRaf = requestAnimationFrame(edgeScrollTick)
  }

  const ensureEdgeScroll = function () {
    if (edgeScrollRaf != null) return
    lastEdgeScrollTs = 0
    edgeScrollRaf = requestAnimationFrame(edgeScrollTick)
  }

  const onItemDragPointer = function (e) {
    const { x, y } = eventXY(e)
    if (typeof x === 'number') lastPointerX = x
    if (typeof y === 'number') {
      lastPointerY = y
      ensureEdgeScroll()
    }
    refreshDragCursor()
  }

  const stopItemDragNudge = function () {
    document.removeEventListener('pointermove', onItemDragPointer, true)
  }

  const onHandlePointerDown = function (e) {
    const item = e.target?.closest?.('.item')
    if (!item || !storyEl.contains(item) || item.classList.contains('shadow-copy')) return
    placeholderBox = { h: item.offsetHeight, w: item.offsetWidth }
  }
  const onHandlePointerUp = function (e) {
    if (Sortable.active) return
    storyEl.querySelectorAll('.wiki-item-drag-sizing').forEach(el => clearPlaceholderBox(el))
    const item = e.target?.closest?.('.item')
    if (item && storyEl.contains(item) && !item.classList.contains('item-placeholder')) {
      clearPlaceholderBox(item)
    } else {
      placeholderBox = null
    }
  }
  storyEl._wikiPlaceholderLock = onHandlePointerDown
  storyEl._wikiPlaceholderUnlock = onHandlePointerUp
  storyEl.addEventListener('pointerdown', onHandlePointerDown, true)
  storyEl.addEventListener('pointerup', onHandlePointerUp, true)
  storyEl.addEventListener('pointercancel', onHandlePointerUp, true)

  const sortable = Sortable.create(storyEl, {
    animation: 0,
    handle: undefined,
    delay: 0,
    distance: 5,
    forceFallback: true,
    fallbackOnBody: true,
    fallbackTolerance: 3,
    // Custom vert edge scroll (desktop+mobile) targets .page; Sortable's scroller misses it.
    scroll: false,
    bubbleScroll: false,
    forceAutoScrollFallback: false,
    // .shadow-copy keeps .item for looks but must not be sortable.
    draggable: '.item:not(.shadow-copy)',
    filter: 'a, button, input, textarea, .item-action-toolbar, .item-edit-button',
    preventOnFilter: false,
    invertSwap: true,
    // 0.5 keeps a dead center so short neighbors don't flip-flop after mid-list snaps.
    invertedSwapThreshold: 0.5,
    emptyInsertThreshold: 48,
    ghostClass: 'item-placeholder',
    chosenClass: 'wiki-item-chosen',
    dragClass: 'wiki-item-drag',
    group: {
      name: 'wiki-story',
      pull() {
        return true
      },
      put(to, from, dragEl) {
        if (!(dragEl?.classList?.contains('item') && !dragEl.classList.contains('shadow-copy'))) return false
        return true
      },
    },
    disabled: !isEditOn(),
    onChoose(evt) {
      const el = evt.item
      if (!el) return
      if (!placeholderBox) {
        placeholderBox = { h: el.offsetHeight, w: el.offsetWidth }
      }
      el.style.willChange = 'auto'
      reserveSlotMinHeight(el)
    },
    onUnchoose(evt) {
      clearPlaceholderBox(evt.item)
    },
    onStart(evt) {
      const syncHandleHelper = () => {
        const helper = document.querySelector('.sortable-fallback.item, .wiki-item-drag')
        if (!helper) return
        syncDragItemTypography(evt.item, helper)
        helper.style.height = 'auto'
        helper.style.maxHeight = 'none'
        helper.style.overflow = 'visible'
      }
      syncHandleHelper()
      requestAnimationFrame(syncHandleHelper)
      try {
        require('./editor').noteDragStarted()
      } catch (_) {
        /* optional */
      }
      try {
        setPageSortableEnabled(false)
      } catch (_) {
        /* optional */
      }
      const $item = $(evt.item)
      originalOrder = getStoryItemOrder(evt.from)
      originalIndex = $('.item').index($item)
      dragCancelled = false
      lastEvent = evt.originalEvent
      shiftHeld = !!evt.originalEvent?.shiftKey
      dragItemSnapshot = $.extend({}, getItem($item))
      dragPageElement = $item.data('pageElement')
      $('body').on('keydown', cancelDrag)
      document.addEventListener('keydown', onShiftKey)
      document.addEventListener('keyup', onShiftKey)
      {
        const { x, y } = eventXY(evt.originalEvent)
        if (typeof x === 'number') lastPointerX = x
        if (typeof y === 'number') lastPointerY = y
      }
      document.body.classList.add('wiki-item-story-dragging')
      document.addEventListener('pointermove', onItemDragPointer, true)
      ensureEdgeScroll()
      lastToStory = null
      const from = evt.from
      const originIndex = evt.oldIndex
      const itemEl = evt.item
      const $shadow = $item
        .clone()
        .removeClass(
          'item-placeholder wiki-item-chosen wiki-item-drag wiki-item-drag-sizing handle-selected toolbar-active is-copy-preview',
        )
        .addClass('shadow-copy')
        .removeAttr('data-id')
        .css({ width: '', height: '', minHeight: '', maxHeight: '', overflow: '', position: '', zIndex: '', display: 'none' })
      const shadowEl = $shadow[0]
      if (from && itemEl.parentNode === from) {
        from.insertBefore(shadowEl, itemEl.nextSibling)
      } else if (from && typeof originIndex === 'number') {
        const ref = from.children[originIndex]
        if (ref) from.insertBefore(shadowEl, ref)
        else from.appendChild(shadowEl)
      } else {
        $shadow.insertAfter($item)
      }
      $('body').css('cursor', 'move')
      $('.shadow-copy').hide().removeClass('is-copy-preview')
      // If Shift was already down at drag start and we leave the page, preview copy.
      if (shiftHeld) refreshDragCursor()
      lockPlaceholderBox(evt.item)
    },
    onMove(evt, originalEvent) {
      if (evt.dragged?.classList?.contains('page')) return false
      if (evt.to?.classList?.contains('main')) return false
      lastEvent = originalEvent
      if (originalEvent && typeof originalEvent.shiftKey === 'boolean') {
        shiftHeld = originalEvent.shiftKey
      }
      const $item = $(evt.dragged)
      if (!$item.data('pageElement') && dragPageElement) {
        $item.data('pageElement', dragPageElement)
      }
      if (!$item.data('item') && dragItemSnapshot) {
        $item.data('item', $.extend({}, dragItemSnapshot))
      }
      const $destPage = $(evt.to).closest('.page')
      const $sourcePage = $item.data('pageElement')
      const shifting = !!(originalEvent?.shiftKey || shiftHeld)
      lastToStory = evt.to
      changeMouseCursor({ shiftKey: shifting }, $item, evt.to)
      const ghost =
        (evt.dragged?.classList?.contains('item-placeholder') && evt.dragged) ||
        evt.to?.querySelector?.('.item-placeholder') ||
        evt.from?.querySelector?.('.item-placeholder')
      if (ghost) lockPlaceholderBox(ghost)
      // Same-page handle reorder on a ghost is allowed; cross-page stays no-drop.
      if ($destPage.hasClass('ghost') && evt.from !== evt.to) return false
      if (
        $sourcePage &&
        !$sourcePage.is($destPage) &&
        $sourcePage.attr('id') === $destPage.attr('id') &&
        !shifting
      ) {
        return false
      }
      return true
    },
    onEnd(evt) {
      clearItemDragCursorClasses()
      $('body').css('cursor', origCursor).off('keydown', cancelDrag)
      $('.wiki-item-drag, .sortable-fallback.item').css('cursor', '')
      document.removeEventListener('keydown', onShiftKey)
      document.removeEventListener('keyup', onShiftKey)
      stopItemDragNudge()
      stopEdgeScroll()
      lastPointerY = null
      lastPointerX = null
      const $item = $(evt.item)
      if (!$item.data('pageElement') && dragPageElement) {
        $item.data('pageElement', dragPageElement)
      }
      if (!$item.data('item') && dragItemSnapshot) {
        $item.data('item', $.extend({}, dragItemSnapshot))
      }
      let shiftKey = !!shiftHeld
      // Final mouseup may still include shiftKey even if our tracker missed it.
      if (evt.originalEvent && typeof evt.originalEvent.shiftKey === 'boolean') {
        shiftKey = evt.originalEvent.shiftKey || shiftHeld
      }
      const revert = () => {
        if (evt.from && evt.oldIndex != null) {
          const ref = evt.from.children[evt.oldIndex]
          if (ref) evt.from.insertBefore(evt.item, ref)
          else evt.from.appendChild(evt.item)
        }
      }
      // If an item somehow landed under .main (page lineup), put it back in its story.
      if ($item.parent().hasClass('main')) {
        revert()
      }
      const crossed = evt.from !== evt.to
      const reordered = evt.oldIndex !== evt.newIndex
      if (dragCancelled) {
        revert()
      } else if (crossed || reordered) {
        handleDrop(lastEvent || {}, $item, originalIndex, originalOrder, {
          shiftKey,
          revert,
        })
      }
      $('.shadow-copy').remove()
      clearPlaceholderBox(evt.item)
      dragItemSnapshot = null
      dragPageElement = null
      shiftHeld = false
      lastToStory = null
      try {
        setPageSortableEnabled(true)
      } catch (_) {
        /* optional */
      }
    },
  })

  storyEl._wikiSortable = sortable
  return sortable
}

const setStorySortableEnabled = function (enabled) {
  const on = !!enabled && isEditOn()
  $('.page .story').each(function () {
    if (this._wikiSortable) {
      this._wikiSortable.option('disabled', !on)
    }
  })
}


const SELECTED_CLASS = 'journal-selected'
const JOURNAL_SELECTED_CLASS = SELECTED_CLASS
const MERGING = 'wiki-journal-merging'

const getPageObject = function (journalEl) {
  const $page = $(journalEl).parents('.page:first')
  return lineup.atKey($page.data('key'))
}


const isJournalSelected = (el) => !!(el && el.classList && el.classList.contains(SELECTED_CLASS))

let suppressClickUntil = 0
const shouldSuppressJournalClick = () => performance.now() < suppressClickUntil

const clearTextSelection = function () {
  try {
    window.getSelection?.()?.removeAllRanges?.()
  } catch (_) {
    /* ignore */
  }
}

const journalUnderPoint = function (x, y, selfEl) {
  const el = document.elementFromPoint(x, y)
  if (!el) return null
  const target = el.closest('.journal')
  if (!target || target === selfEl) return null
  return target
}

const clearMergeHover = function () {
  document.querySelectorAll('.journal.ui-state-hover').forEach((el) => {
    el.classList.remove('ui-state-hover')
  })
}

const handleMerging = function (sourceEl, destEl) {
  if (!sourceEl || !destEl || sourceEl === destEl) return false
  const source = getPageObject(sourceEl)
  const dest = getPageObject(destEl)
  if (!source || !dest || source === dest) return false
  const merged = dest.merge(source)
  if (!merged) return false
  pageEmitter.dispatchEvent(new CustomEvent('show', { detail: merged }))
  return true
}

// --- Desktop: immediate drag after 12px move (stock UX) ---

const bindJournalMerge = function ($page) {
  const journalEl = $page.find('.journal')[0]
  if (!journalEl) return

  if (journalEl._wikiMergeBound) return
  journalEl._wikiMergeBound = true
  journalEl.classList.add('journal-merge-source')

  let drag = null
  let edgeScrollRaf = null
  let lastEdgeScrollTs = 0
  let pointerX = null
  let pointerY = null
  const mainEl = () => getMain()

  const preventSelect = function (e) {
    e.preventDefault()
  }

  const stopEdgeScroll = function () {
    if (edgeScrollRaf != null) {
      cancelAnimationFrame(edgeScrollRaf)
      edgeScrollRaf = null
    }
    lastEdgeScrollTs = 0
  }

  const refreshJournalHover = function () {
    if (typeof pointerX !== 'number' || typeof pointerY !== 'number') return
    clearMergeHover()
    const over = journalUnderPoint(pointerX, pointerY, journalEl)
    if (over) over.classList.add('ui-state-hover')
    if (drag) drag.over = over
  }

  const edgeScrollTick = function (ts) {
    edgeScrollRaf = null
    if (!drag?.active) return
    if (!lastEdgeScrollTs) lastEdgeScrollTs = ts
    const dt = Math.min(0.05, (ts - lastEdgeScrollTs) / 1000)
    lastEdgeScrollTs = ts

    const main = mainEl()
    const { dir, speed } = scrollLineupNearEdge(pointerX, main)
    if (main && dir && speed > 0) {
      main.scrollLeft += dir * speed * dt
    }

    const destPage = drag.over?.closest?.('.page')
    const candidates = vertScrollCandidates(
      pointerX,
      journalEl.closest('.page'),
      destPage?.querySelector?.('.story'),
    )
    if (destPage && !candidates.includes(destPage)) candidates.push(destPage)
    scrollPageNearEdge(candidates, pointerY, dt, VERT_EDGE_PX, VERT_SCROLL_PX_PER_SEC)
    refreshJournalHover()

    edgeScrollRaf = requestAnimationFrame(edgeScrollTick)
  }

  const startEdgeScroll = function () {
    if (edgeScrollRaf != null) return
    lastEdgeScrollTs = 0
    edgeScrollRaf = requestAnimationFrame(edgeScrollTick)
  }

  const cleanup = function () {
    if (!drag) return
    stopEdgeScroll()
    drag.clone?.remove()
    journalEl.classList.remove('is-dragging')
    document.body.classList.remove(MERGING)
    clearMergeHover()
    document.removeEventListener('pointermove', onMove)
    document.removeEventListener('pointerup', onUp)
    document.removeEventListener('pointercancel', onCancel)
    document.removeEventListener('selectstart', preventSelect, true)
    if (drag.pointerId != null) {
      try {
        journalEl.releasePointerCapture?.(drag.pointerId)
      } catch (_) {
        /* already released */
      }
    }
    drag = null
    pointerX = null
    pointerY = null
  }

  const onMove = function (e) {
    if (!drag || e.pointerId !== drag.pointerId) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    if (!drag.active) {
      if (Math.abs(dx) < 12 && Math.abs(dy) < 12) return
      drag.active = true
      journalEl.classList.add('is-dragging')
      document.body.classList.add(MERGING)
      clearTextSelection()
      try {
        journalEl.setPointerCapture?.(drag.pointerId)
      } catch (_) {
        /* ignore */
      }
      const rect = journalEl.getBoundingClientRect()
      const clone = journalEl.cloneNode(true)
      clone.classList.add('journal-merge-clone')
      clone.classList.remove(SELECTED_CLASS)
      clone.style.position = 'fixed'
      clone.style.left = `${rect.left}px`
      clone.style.top = `${rect.top}px`
      clone.style.width = `${rect.width}px`
      clone.style.pointerEvents = 'none'
      clone.style.zIndex = '1000'
      clone.style.opacity = '0.85'
      document.querySelector('.main')?.appendChild(clone)
      drag.clone = clone
      drag.offsetX = e.clientX - rect.left
      drag.offsetY = e.clientY - rect.top
    }
    drag.clone.style.left = `${e.clientX - drag.offsetX}px`
    drag.clone.style.top = `${e.clientY - drag.offsetY}px`

    pointerX = e.clientX
    pointerY = e.clientY
    refreshJournalHover()
    if (drag.active) startEdgeScroll()
    e.preventDefault()
  }

  const onUp = function (e) {
    if (!drag || (e && e.pointerId !== drag.pointerId)) return
    const wasActive = drag.active
    if (wasActive) {
      const over =
        (typeof e.clientX === 'number' && journalUnderPoint(e.clientX, e.clientY, journalEl)) ||
        drag.over
      if (over) handleMerging(journalEl, over)
      const suppressClick = function (ev) {
        ev.preventDefault()
        ev.stopPropagation()
        journalEl.removeEventListener('click', suppressClick, true)
      }
      journalEl.addEventListener('click', suppressClick, true)
      setTimeout(() => journalEl.removeEventListener('click', suppressClick, true), 0)
    }
    cleanup()
  }

  const onCancel = function () {
    cleanup()
  }

  journalEl.addEventListener('pointerdown', function (e) {
    if (!isEditOn()) return
    // Desktop immediate drag after 12px move.
    if (e.button != null && e.button !== 0) return
    clearTextSelection()
    document.addEventListener('selectstart', preventSelect, true)
    drag = {
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      startX: e.clientX,
      startY: e.clientY,
      active: false,
      clone: null,
      over: null,
    }
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
    document.addEventListener('pointercancel', onCancel)
  })
}


const mobileHandlers = {
  bindMainSortable: null,
  bindStorySortable: null,
  bindJournalMerge: null,
  afterPageReady: null,
}

const setMobileHandlers = function (partial = {}) {
  Object.assign(mobileHandlers, partial)
}

const initPageSortable = mainEl =>
  (mobileHandlers.bindMainSortable || bindMainSortable)(mainEl)

const initDragging = $page =>
  (mobileHandlers.bindStorySortable || bindStorySortable)($page)

const initMerging = $page =>
  (mobileHandlers.bindJournalMerge || bindJournalMerge)($page)

// Public surface for stock call sites (legacy/refresh). Mobile/parity hooks
// and geometry helpers stay file-local and are passed via deps / setMobileHandlers.
module.exports = {
  initPageSortable,
  initDragging,
  initMerging,
}
